import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Loader2,
  ArrowLeft,
  Users,
  Store,
  ShoppingCart,
  Mail,
  Phone,
  UserRound,
  ChevronLeft,
  ChevronRight,
  Layers
} from 'lucide-react';
import { Box } from '@mui/material';
import { useAuth } from '@/shared/contexts';
import {
  superAdminApi,
  type SuperAdminSupplierDetail,
  type SuperAdminUserSummary,
  type SuperAdminRestaurantSummary
} from '@/pages/SuperAdmin/api/superAdminApi';
import { SaDataTable, type SaColumn } from '@/pages/SuperAdmin/SaDataTable';
import { SaNeutralChip, SaStatusChip } from '@/pages/SuperAdmin/SaStatusChip';
import { Alert } from '@/shared/components/ui/Alert';
import { Button } from '@/shared/components/ui/Button';
import {
  OrderCreationCodeDialog,
  type OrderSessionPayload
} from '@/pages/SuperAdmin/OrderCreationCodeDialog';

type TabId = 'overview' | 'employees' | 'restaurants' | 'orders';

type EmployeeRow = SuperAdminUserSummary & { isSelf: boolean };
type RestaurantRow = SuperAdminRestaurantSummary & { linkedAt: string };
type OrderRow = SuperAdminSupplierDetail['orders']['items'][number];

export const SuperAdminSupplierDetailPage: React.FC = () => {
  const { supplierId } = useParams<{ supplierId: string }>();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>('overview');
  const [detail, setDetail] = useState<SuperAdminSupplierDetail | null>(null);
  const [ordersPage, setOrdersPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);

  const load = useCallback(async () => {
    if (!accessToken || !supplierId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await superAdminApi.getSupplierDetail(accessToken, supplierId, {
        ordersPage,
        ordersLimit: 20
      });
      setDetail(res.data);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to load supplier';
      setError(Array.isArray(message) ? message.join(', ') : String(message));
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, supplierId, ordersPage]);

  useEffect(() => {
    load();
  }, [load]);

  const employeeColumns = useMemo<SaColumn<EmployeeRow>[]>(
    () => [
      {
        id: 'employee',
        label: 'Employee',
        width: '28%',
        render: row => {
          const name =
            [row.firstName, row.lastName].filter(Boolean).join(' ') ||
            row.businessName ||
            '—';
          return (
            <Box sx={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
              {name}
              {row.isSelf && (
                <Box component="span" sx={{ ml: 1, display: 'inline-flex' }}>
                  <SaNeutralChip label="Self" />
                </Box>
              )}
            </Box>
          );
        }
      },
      {
        id: 'email',
        label: 'Email',
        width: '28%',
        render: row => row.email || '—'
      },
      {
        id: 'phone',
        label: 'Phone',
        width: '16%',
        render: row => row.phone || '—'
      },
      {
        id: 'status',
        label: 'Status',
        width: '14%',
        render: row => <SaStatusChip status={row.status} />
      },
      {
        id: 'type',
        label: 'Type',
        width: '14%',
        render: row => (
          <Box sx={{ textTransform: 'capitalize' }}>
            {row.role?.replace(/_/g, ' ') || '—'}
          </Box>
        )
      }
    ],
    []
  );

  const restaurantColumns = useMemo<SaColumn<RestaurantRow>[]>(
    () => [
      {
        id: 'restaurant',
        label: 'Restaurant',
        width: '34%',
        render: row =>
          row.businessName ||
          [row.firstName, row.lastName].filter(Boolean).join(' ') ||
          '—'
      },
      {
        id: 'email',
        label: 'Email',
        width: '34%',
        render: row => row.email || '—'
      },
      {
        id: 'status',
        label: 'Status',
        width: '16%',
        render: row => <SaStatusChip status={row.status} />
      },
      {
        id: 'linked',
        label: 'Linked',
        width: '16%',
        render: row =>
          row.linkedAt ? new Date(row.linkedAt).toLocaleDateString() : '—'
      }
    ],
    []
  );

  const orderColumns = useMemo<SaColumn<OrderRow>[]>(
    () => [
      {
        id: 'orderNumber',
        label: 'Order #',
        width: '16%',
        render: row => (
          <Box sx={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{row.orderNumber}</Box>
        )
      },
      {
        id: 'name',
        label: 'Name',
        width: '22%',
        render: row => row.orderName || '—'
      },
      {
        id: 'restaurant',
        label: 'Restaurant',
        width: '26%',
        render: row => row.restaurantName || '—'
      },
      {
        id: 'status',
        label: 'Status',
        width: '16%',
        render: row => <SaStatusChip status={row.status} />
      },
      {
        id: 'created',
        label: 'Created',
        width: '20%',
        render: row =>
          row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'
      }
    ],
    []
  );

  if (loading && !detail) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-[#006B82]" />
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">{error}</Alert>
        <Button type="button" variant="outline" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </div>
    );
  }

  if (!detail) return null;

  const { supplier, employees, restaurants, orders, counts } = detail;
  const displayName =
    supplier.businessName ||
    [supplier.firstName, supplier.lastName].filter(Boolean).join(' ') ||
    'Supplier';

  const tabs: {
    id: TabId;
    label: string;
    count?: number;
    icon: React.ReactNode;
  }[] = [
    { id: 'overview', label: 'Overview', icon: <UserRound className="h-4 w-4" /> },
    {
      id: 'employees',
      label: 'Employees',
      count: counts.employees,
      icon: <Users className="h-4 w-4" />
    },
    {
      id: 'restaurants',
      label: 'Restaurants',
      count: counts.restaurants,
      icon: <Store className="h-4 w-4" />
    },
    {
      id: 'orders',
      label: 'Orders',
      count: counts.orders,
      icon: <ShoppingCart className="h-4 w-4" />
    }
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/super-admin/suppliers"
            className="mb-2 inline-flex items-center gap-1 text-sm text-text-tertiary hover:text-[#006B82]"
          >
            <ArrowLeft className="h-4 w-4" />
            Suppliers
          </Link>
          <h2 className="text-2xl font-semibold tracking-tight text-text-primary">
            {displayName}
          </h2>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-tertiary">
            <SaStatusChip status={supplier.status} />
            {supplier.email && (
              <span className="inline-flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" />
                {supplier.email}
              </span>
            )}
            {supplier.phone && (
              <span className="inline-flex items-center gap-1">
                <Phone className="h-3.5 w-3.5" />
                {supplier.phone}
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              navigate(`/super-admin/suppliers/${supplier.id}/catalog`)
            }
          >
            <Layers className="mr-1.5 h-4 w-4" />
            Sections & categories
          </Button>
          <Button
            type="button"
            className="bg-[#006B82] hover:bg-[#005a6e]"
            disabled={supplier.status !== 'active'}
            title={
              supplier.status !== 'active'
                ? 'Activate this supplier before placing an order'
                : undefined
            }
            onClick={() => setCodeDialogOpen(true)}
          >
            Place order
          </Button>
        </div>
      </div>

      <OrderCreationCodeDialog
        open={codeDialogOpen}
        supplierId={supplier.id}
        onClose={() => setCodeDialogOpen(false)}
        onSuccess={(session: OrderSessionPayload) => {
          setCodeDialogOpen(false);
          navigate(`/super-admin/suppliers/${supplier.id}/orders`, {
            state: { orderSession: session }
          });
        }}
      />

      {supplier.status !== 'active' && (
        <Alert variant="destructive">
          Supplier is {supplier.status}. Activate the account before placing an
          order.
        </Alert>
      )}

      {error && <Alert variant="destructive">{error}</Alert>}

      <div className="flex flex-wrap gap-1 border-b border-border-primary">
        {tabs.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={[
              'inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
              tab === t.id
                ? 'border-[#006B82] text-[#006B82]'
                : 'border-transparent text-text-tertiary hover:text-text-primary'
            ].join(' ')}
          >
            {t.icon}
            {t.label}
            {typeof t.count === 'number' && (
              <span className="rounded-full bg-background-tertiary px-2 py-0.5 text-xs text-text-secondary">
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-border-primary bg-background-card p-5 shadow-sm">
            <p className="text-sm text-text-tertiary">Employees</p>
            <p className="mt-2 text-3xl font-semibold text-text-primary">
              {counts.employees}
            </p>
          </div>
          <div className="rounded-2xl border border-border-primary bg-background-card p-5 shadow-sm">
            <p className="text-sm text-text-tertiary">Restaurants</p>
            <p className="mt-2 text-3xl font-semibold text-text-primary">
              {counts.restaurants}
            </p>
          </div>
          <div className="rounded-2xl border border-border-primary bg-background-card p-5 shadow-sm">
            <p className="text-sm text-text-tertiary">Orders</p>
            <p className="mt-2 text-3xl font-semibold text-text-primary">
              {counts.orders}
            </p>
          </div>
          <div className="sm:col-span-3 rounded-2xl border border-border-primary bg-background-card p-5 shadow-sm">
            <h3 className="font-semibold text-text-primary">Account</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wide text-text-muted">
                  First name
                </p>
                <p className="mt-1 font-medium text-text-primary">
                  {supplier.firstName || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-text-muted">
                  Last name
                </p>
                <p className="mt-1 font-medium text-text-primary">
                  {supplier.lastName || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-text-muted">
                  Role
                </p>
                <p className="mt-1 font-medium capitalize text-text-primary">
                  {supplier.role?.replace(/_/g, ' ') || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-text-muted">
                  Created
                </p>
                <p className="mt-1 font-medium text-text-primary">
                  {supplier.createdAt
                    ? new Date(supplier.createdAt).toLocaleString()
                    : '—'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'employees' && (
        <SaDataTable
          columns={employeeColumns}
          rows={employees}
          getRowId={row => row.id}
          emptyMessage="No employees linked to this supplier"
        />
      )}

      {tab === 'restaurants' && (
        <SaDataTable
          columns={restaurantColumns}
          rows={restaurants}
          getRowId={row => row.id}
          emptyMessage="No restaurants linked to this supplier"
        />
      )}

      {tab === 'orders' &&
        (loading ? (
          <div className="flex justify-center rounded-2xl border border-border-primary bg-background-card py-12 shadow-sm">
            <Loader2 className="h-6 w-6 animate-spin text-[#006B82]" />
          </div>
        ) : (
          <div className="space-y-3">
            <SaDataTable
              columns={orderColumns}
              rows={orders.items}
              getRowId={row => row.id}
              emptyMessage="No orders for this supplier"
            />
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-text-secondary">
              <span>
                {orders.total} order{orders.total === 1 ? '' : 's'} · Page{' '}
                {orders.page} of {orders.totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  disabled={orders.page <= 1}
                  onClick={() => setOrdersPage(p => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  disabled={orders.page >= orders.totalPages}
                  onClick={() => setOrdersPage(p => p + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
};
